/**
 * The resume synonym guides: /resume-synonyms/<slug>, one page per overused
 * resume word, plus the hub at /resume-synonyms.
 *
 * This list is the index: the hub, the sitemap, related-word links and the
 * route's 404 check read it. The content of each page lives in
 * data/resume-synonyms/<slug>.json (one file per word, loaded only by its own
 * page), in Czech in data/resume-synonyms/cs/<slug>.json, in German in
 * data/resume-synonyms/de/<slug>.json, and
 * tests/constants/resumeSynonyms.test.ts keeps them in step.
 *
 * Picked by US search volume for "synonym for <word>" (DataForSEO, checked
 * 2026-09-29). A generic thesaurus owns the dictionary intent; these pages are
 * about which synonym fits which situation on a resume, with a before-and-after
 * bullet for each — so add a word only when it is a real resume word and the
 * entry can say something a thesaurus cannot.
 */
export interface ResumeSynonymIndexItem {
  slug: string
  /** Display form, as it would start a bullet. */
  word: string
  /** The Czech page's word (data/resume-synonyms/cs), in the same form. */
  wordCs: string
  /** The German page's word (data/resume-synonyms/de), in the same form. */
  wordDe: string
  /** "verb" words start bullets; "adjective" words sit in the summary. */
  kind: 'verb' | 'adjective' | 'phrase'
  /** US monthly searches for "synonym for <word>", for ordering the hub. */
  volume: number
}

export const RESUME_SYNONYMS: ResumeSynonymIndexItem[] = [
  { slug: 'experienced', word: 'Experienced', wordCs: 'Zkušený', wordDe: 'Erfahren', kind: 'adjective', volume: 135000 },
  { slug: 'supported', word: 'Supported', wordCs: 'Podporoval', wordDe: 'Unterstützt', kind: 'verb', volume: 135000 },
  { slug: 'helped', word: 'Helped', wordCs: 'Pomáhal', wordDe: 'Geholfen', kind: 'verb', volume: 90500 },
  { slug: 'created', word: 'Created', wordCs: 'Vytvořil', wordDe: 'Erstellt', kind: 'verb', volume: 74000 },
  { slug: 'improved', word: 'Improved', wordCs: 'Zlepšil', wordDe: 'Verbessert', kind: 'verb', volume: 74000 },
  { slug: 'developed', word: 'Developed', wordCs: 'Vyvinul', wordDe: 'Entwickelt', kind: 'verb', volume: 60500 },
  { slug: 'analyzed', word: 'Analyzed', wordCs: 'Analyzoval', wordDe: 'Analysiert', kind: 'verb', volume: 60500 },
  { slug: 'highlighted', word: 'Highlighted', wordCs: 'Upozornil', wordDe: 'Hervorgehoben', kind: 'verb', volume: 60500 },
  { slug: 'focused', word: 'Focused', wordCs: 'Soustředěný', wordDe: 'Fokussiert', kind: 'adjective', volume: 49500 },
  { slug: 'passionate', word: 'Passionate', wordCs: 'Nadšený', wordDe: 'Leidenschaftlich', kind: 'adjective', volume: 49500 },
  { slug: 'worked', word: 'Worked', wordCs: 'Pracoval', wordDe: 'Gearbeitet', kind: 'verb', volume: 49500 },
  { slug: 'learned', word: 'Learned', wordCs: 'Naučil se', wordDe: 'Gelernt', kind: 'verb', volume: 49500 },
  { slug: 'demonstrated', word: 'Demonstrated', wordCs: 'Prokázal', wordDe: 'Bewiesen', kind: 'verb', volume: 49500 },
  { slug: 'fostered', word: 'Fostered', wordCs: 'Pěstoval', wordDe: 'Gefördert', kind: 'verb', volume: 49500 },
  { slug: 'maintained', word: 'Maintained', wordCs: 'Udržoval', wordDe: 'Gepflegt', kind: 'verb', volume: 40500 },
  { slug: 'implemented', word: 'Implemented', wordCs: 'Implementoval', wordDe: 'Implementiert', kind: 'verb', volume: 40500 },
  { slug: 'contributed', word: 'Contributed', wordCs: 'Přispěl', wordDe: 'Beigetragen', kind: 'verb', volume: 40500 },
  { slug: 'communicated', word: 'Communicated', wordCs: 'Komunikoval', wordDe: 'Kommuniziert', kind: 'verb', volume: 40500 },
  { slug: 'transformed', word: 'Transformed', wordCs: 'Transformoval', wordDe: 'Transformiert', kind: 'verb', volume: 40500 },
  { slug: 'presented', word: 'Presented', wordCs: 'Prezentoval', wordDe: 'Präsentiert', kind: 'verb', volume: 40500 },
  { slug: 'increased', word: 'Increased', wordCs: 'Zvýšil', wordDe: 'Gesteigert', kind: 'verb', volume: 40500 },
  { slug: 'utilize', word: 'Utilize', wordCs: 'Využívám', wordDe: 'Nutzen', kind: 'verb', volume: 40500 },
  { slug: 'hard-working', word: 'Hard-working', wordCs: 'Pracovitý', wordDe: 'Fleißig', kind: 'adjective', volume: 40500 },
  { slug: 'enhanced', word: 'Enhanced', wordCs: 'Vylepšil', wordDe: 'Optimiert', kind: 'verb', volume: 33100 },
  { slug: 'established', word: 'Established', wordCs: 'Založil', wordDe: 'Aufgebaut', kind: 'verb', volume: 33100 },
  // The page is about replacing "Responsible for …"; the query is the bare word.
  { slug: 'responsible', word: 'Responsible', wordCs: 'Zodpovědný', wordDe: 'Verantwortlich', kind: 'phrase', volume: 33100 },
  { slug: 'managed', word: 'Managed', wordCs: 'Řídil', wordDe: 'Geleitet', kind: 'verb', volume: 27100 },
  { slug: 'organized', word: 'Organized', wordCs: 'Zorganizoval', wordDe: 'Organisiert', kind: 'verb', volume: 27100 },
  { slug: 'identified', word: 'Identified', wordCs: 'Identifikoval', wordDe: 'Identifiziert', kind: 'verb', volume: 27100 },
  { slug: 'completed', word: 'Completed', wordCs: 'Dokončil', wordDe: 'Abgeschlossen', kind: 'verb', volume: 27100 },
  { slug: 'reduced', word: 'Reduced', wordCs: 'Snížil', wordDe: 'Reduziert', kind: 'verb', volume: 27100 },
  { slug: 'ensure', word: 'Ensure', wordCs: 'Zajišťuji', wordDe: 'Sicherstellen', kind: 'verb', volume: 22200 },
  { slug: 'strengthened', word: 'Strengthened', wordCs: 'Posílil', wordDe: 'Gestärkt', kind: 'verb', volume: 22200 },
  { slug: 'evaluated', word: 'Evaluated', wordCs: 'Hodnotil', wordDe: 'Bewertet', kind: 'verb', volume: 22200 },
  { slug: 'driven', word: 'Driven', wordCs: 'Ambiciózní', wordDe: 'Ehrgeizig', kind: 'adjective', volume: 22200 },
  { slug: 'designed', word: 'Designed', wordCs: 'Navrhl', wordDe: 'Entworfen', kind: 'verb', volume: 22200 },
  { slug: 'assisted', word: 'Assisted', wordCs: 'Asistoval', wordDe: 'Assistiert', kind: 'verb', volume: 18100 },
  { slug: 'achieved', word: 'Achieved', wordCs: 'Dosáhl', wordDe: 'Erreicht', kind: 'verb', volume: 18100 },
  { slug: 'led', word: 'Led', wordCs: 'Vedl', wordDe: 'Geführt', kind: 'verb', volume: 12100 },
  { slug: 'collaborated', word: 'Collaborated', wordCs: 'Spolupracoval', wordDe: 'Zusammengearbeitet', kind: 'verb', volume: 12100 },
]

export const RESUME_SYNONYM_SLUGS = RESUME_SYNONYMS.map(item => item.slug)

export function findResumeSynonym(slug: string): ResumeSynonymIndexItem | undefined {
  return RESUME_SYNONYMS.find(item => item.slug === slug)
}

/** Content of one page, data/resume-synonyms/<slug>.json. */
export interface ResumeSynonymEntry {
  slug: string
  word: string
  /** Two or three sentences on why this word is weak on a resume, and the fix. */
  intro: string
  /** The different things people mean by the word on a resume. */
  senses: Array<{
    id: string
    /** "When you …" — the situation, not a definition. */
    label: string
    synonyms: Array<{ word: string, note: string }>
    example: { before: string, after: string }
  }>
  /** Thesaurus synonyms that sound wrong on a resume, and why. */
  avoid?: string
  tip: string
  /** Slugs from RESUME_SYNONYMS. */
  related: string[]
}

/** The word in the visitor's language. */
export function synonymWord(item: ResumeSynonymIndexItem, locale: string): string {
  if (locale === 'cs') return item.wordCs
  if (locale === 'de') return item.wordDe
  return item.word
}

export function synonymCount(entry: ResumeSynonymEntry): number {
  return new Set(entry.senses.flatMap(sense => sense.synonyms.map(s => s.word.toLowerCase()))).size
}
